import Collaboration, { isChangeOrigin } from '@tiptap/extension-collaboration';
import CollaborationCaret from '@tiptap/extension-collaboration-caret';
import { EditorContent, useEditor } from '@tiptap/react';
import { useEffect, useRef } from 'react';
import type * as Y from 'yjs';
import { SILENT, updateCard } from '../../shared/ops.js';
import { noteExtensions } from '../../shared/richtext.js';
import type { ProjectConn } from '../doc/useProject.js';

/**
 * Rich text bound to the card's Y.XmlFragment. Viewing uses a read-only editor; editing adds live carets.
 * Undo inside the text is Tiptap's collaborative undo (StarterKit's own undo is disabled).
 */
export function NoteCard({ conn, card, editing, onDone }: { conn: ProjectConn; card: Y.Map<any>; editing: boolean; onDone: () => void }) {
  const fragment = card.get('body') as Y.XmlFragment;
  // Only this edit session's own changes should cause a blur stamp — merely clicking into a note
  // and out again, with nothing typed, must not log "edited 1 card" or touch updatedAt/updatedBy
  // (review Minor 4). Reset per editor instance (onCreate), set as soon as the content changes.
  const changed = useRef(false);
  const editor = useEditor(
    {
      editable: editing,
      autofocus: editing ? 'end' : false,
      extensions: [
        ...noteExtensions(), // the same schema the server uses for agents' Markdown
        Collaboration.configure({ fragment }),
        ...(editing ? [CollaborationCaret.configure({ provider: conn.provider, user: { name: conn.me.name, color: conn.colour } })] : []),
      ],
      editorProps: { attributes: { class: 'note-text', 'aria-label': 'Note text' } },
      onCreate: () => {
        changed.current = false;
      },
      onUpdate: ({ transaction }) => {
        // Someone else's typing arrives as a change-origin transaction. It isn't this person's edit,
        // so it must not make their blur stamp them as the note's last editor.
        if (!isChangeOrigin(transaction)) changed.current = true;
      },
      onBlur: () => {
        // SILENT: this stamp has no visible change for ⌘Z to show, and must not eat the user's
        // undo step for whatever they actually did.
        if (changed.current) updateCard(conn.doc, conn.me, card.get('id'), {}, SILENT);
        changed.current = false;
        onDone();
      },
    },
    [editing, fragment],
  );
  useEffect(() => {
    if (!editing || !editor) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && editor.commands.blur();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, editor]);
  return <EditorContent editor={editor} className={editing ? 'note editing' : 'note'} />;
}
